import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./WhoWeAre.css";

// Reuse your background video for visual consistency
import webmobbackground from "../../assets/webmobbackground.mp4";

gsap.registerPlugin(ScrollTrigger);

const WhoWeAre = () => {
  const titleRef = useRef(null);

  useEffect(() => {
    const letters = titleRef.current?.querySelectorAll(".letter") || [];

    // Hero title + subtitle
    gsap.from(".mad-gradient-title", {
      scrollTrigger: { trigger: ".mad-gradient-title", start: "top 80%", scrub: 1 },
      scale: 1.3,
      opacity: 0,
      duration: 1,
      ease: "ease.out",
    });

    gsap.from(".mad-subtitle", {
      scrollTrigger: { trigger: ".mad-gradient-title", start: "top 80%", scrub: 1 },
      scale: 0.5,
      opacity: 0,
      duration: 1,
      ease: "ease.out",
    });

    // Letter-by-letter reveal
    gsap.fromTo(
      letters,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.1, stagger: 0.05, ease: "power3.out", delay: 0.4 }
    );

    // Section headings
    gsap.fromTo(
      ".mad-heading",
      { x: -200 },
      {
        x: 0,
        duration: 0.1,
        stagger: 0.05,
        ease: "power3.out",
        delay: 0.4,
        scrollTrigger: { trigger: ".mad-heading", start: "top 80%", scrub: 1 },
      }
    );

    // Intro content
    gsap.from(".mad-text-section > *", {
      scrollTrigger: { trigger: ".mad-text-section", start: "top 80%", scrub: 1 },
      opacity: 0,
      y: 30,
      stagger: 0.15,
      duration: 0.8,
      ease: "bounce.out",
    });

    // Feature list (Mission/Vision/Values…)
    gsap.from(".mad-feature-list .feature-item", {
      scrollTrigger: { trigger: ".mad-feature-list", start: "top 80%", scrub: 1 },
      opacity: 0,
      x: -30,
      stagger: 0.12,
      duration: 0.7,
      ease: "bounce.out",
    });

    // Timeline / Journey
    gsap.from(".mad-process-section .process-step", {
      scrollTrigger: { trigger: ".mad-process-section", start: "top 85%" },
      opacity: 0,
      y: 20,
      stagger: 0.12,
      duration: 0.6,
      ease: "power2.out",
    });

    // Team cards
    gsap.from(".mad-team-grid .mad-team-card", {
      scrollTrigger: { trigger: ".mad-team-section", start: "top 85%", scrub: 1 },
      opacity: 0,
      y: 40,
      stagger: 0.12,
      duration: 0.6,
      ease: "power2.out",
    });

    // Why Choose Us
    gsap.from(".why-item", {
      scrollTrigger: { trigger: ".mad-why-section", start: "top 85%", scrub: 1 },
      opacity: 0,
      y: 200,
      scale: 0.3,
      ease: "ease.out",
    });

    // CTA
    gsap.from(".mad-cta", {
      scrollTrigger: { trigger: ".mad-cta", start: "top 90%" },
      opacity: 0,
      y: 20,
      duration: 0.6,
      ease: "power2.out",
    });
  }, []);

  const title = "WHO WE ARE";

  return (
    <div>
      {/* Background Video */}
      <video className="mad-back" autoPlay muted loop playsInline>
        <source src={webmobbackground} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div className="mad-page">
        {/* Hero */}
        <section className="mad-hero-section">
          <div className="mad-hero-content">
            <h1 className="mad-gradient-title" ref={titleRef}>
              {title.split("").map((c, i) => (
                <span key={i} className="letter">
                  {c === " " ? "\u00A0" : c}
                </span>
              ))}
            </h1>
            <p className="mad-subtitle">Strategy • Creativity • Performance</p>
          </div>
        </section>

        {/* Intro */}
        <section className="mad-text-section">
          <h2 className="mad-heading">We’re Your Growth Partner</h2>
          <p className="mad-paragraph">
            Soaring High combines strategy, creative craft, and performance marketing to help brands
            communicate clearly and grow confidently. From discovery to launch (and optimization),
            we build systems that turn attention into measurable results.
          </p>
        </section>

        {/* What Defines Us (Mission / Vision / Values / Principles) */}
        <section className="mad-feature-list">
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Our Mission:</strong> Empower businesses with data-driven marketing and
              standout creative that moves the needle.
            </p>
          </div>
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Our Vision:</strong> Be the region’s most trusted growth partner—where
              strategy, design, and technology meet.
            </p>
          </div>
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Our Values:</strong> Clarity over noise, Integrity over shortcuts, Craft over
              compromise, Results over vanity metrics.
            </p>
          </div>
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Quality & Delivery:</strong> WCAG-aware design, Core Web Vitals focus, and a
              rigorous QA checklist for accessibility and security.
            </p>
          </div>
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Data First:</strong> We measure what matters, iterate quickly, and align on
              KPIs that reflect real business outcomes.
            </p>
          </div>
          <div className="feature-item">
            <div className="feature-line" />
            <p>
              <strong>Partnership:</strong> Transparent communication, clear timelines, and
              end-to-end support—from brand to performance.
            </p>
          </div>
        </section>

        {/* Our Journey (Timeline) */}
        {/* <section className="mad-process-section">
          <h2 className="mad-heading">Our Journey</h2>
          <ol className="process-timeline">
            <li className="process-step">
              <span className="dot" />
              <div>
                <h3>2019 — Beginning</h3>
                <p>Started with brand identity and landing-page performance projects.</p>
              </div>
            </li>
            <li className="process-step">
              <span className="dot" />
              <div>
                <h3>2021 — Expansion</h3>
                <p>Added full-funnel marketing, content, analytics, and CRO capabilities.</p>
              </div>
            </li>
            <li className="process-step">
              <span className="dot" />
              <div>
                <h3>2023 — Partnerships</h3>
                <p>Scaled delivery with strategic partnerships across KSA & UAE.</p>
              </div>
            </li>
            <li className="process-step">
              <span className="dot" />
              <div>
                <h3>2025 — Today</h3>
                <p>Integrated creative + performance engine delivering measurable growth.</p>
              </div>
            </li>
          </ol>
        </section> */}

        {/* Team (text cards to match no-image look) */}
        <section className="mad-team-section">
          <h2 className="mad-heading">Leadership</h2>
          <div className="mad-team-grid">
            <article className="mad-team-card">
              <h4>Creative Director</h4>
              <p>Leads brand systems and campaign art direction.</p>
            </article>
            <article className="mad-team-card">
              <h4>Strategy Lead</h4>
              <p>Owns discovery, positioning, and growth frameworks.</p>
            </article>
            <article className="mad-team-card">
              <h4>Performance Manager</h4>
              <p>Drives KPI alignment, channel ops, and reporting.</p>
            </article>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mad-why-section">
          <h2 className="mad-heading">Why Choose Us?</h2>
          <div className="why-grid">
            <div className="why-item">UAE & KSA market expertise</div>
            <div className="why-item">Design + data under one roof</div>
            <div className="why-item">Transparent reporting & SLAs</div>
            <div className="why-item">From strategy to performance</div>
          </div>
        </section>

        {/* CTA */}
        <section className="mad-cta">
          <h3>Let’s build something that moves the metrics.</h3>
          <p>Talk to our team about brand, content, and performance—aligned to your KPIs.</p>
          <a href="/contact" className="mad-btn">Talk to Us</a>
        </section>
      </div>
    </div>
  );
};

export default WhoWeAre;
